import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePrimeHub } from "../../../lib/kitVariant";
import type {
  FriendMachine,
  KernelEvent,
  LoadRequest,
  ModeDefinition,
  RunBundle,
  RuntimeConfig,
} from "@picnic-field/local-host/protocol";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import { latestSnapshot } from "../../../lib/api/fieldApi";
import type { Snapshot } from "../../../lib/api/fieldApi";
import { toApiError } from "../../../lib/http/errors";
import type { ErrorCode } from "../../../lib/http/errors";
import type { GalleryItem, Sticker } from "../../../lib/api/socialApi";
import { useSocialApi } from "../../../lib/api/socialApi";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { ButtonLink } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { PicoBubble, usePicoToast } from "../../../components/PicoBubble";
import { usePico } from "../../pico";
import { usePerfTelemetry } from "../../../hooks/useTelemetry";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useCourseApi } from "../../../lib/api/courseApi";
import type { CourseApi } from "../../../lib/api/courseApi";
import { CourseScene } from "../../course";
import { PrincipleLegend, useHostPrinciple } from "../../course";
import { courseDeps } from "../../course";
import type { CourseViewLike, GalleryViewLike } from "../../course";
import { hasProgram } from "../../course";
import { sendInput, useHost } from "../../course";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { RemoteOverlay } from "../../remote";
import { useRemote } from "../../remote";
import { rememberMyNickname } from "../../../hooks/useMyNickname";
import { relayMotion, type RelayMotion } from "../../../lib/programMotion";
import { useSession } from "../../../store/session/SessionProvider";
import { workTitle } from "../../upload";
import { ExhibitCard } from "./ExhibitCard";
import { FriendsPanel } from "./FriendsPanel";
import { GalleryScene } from "./GalleryScene";
import { PlayHud } from "./PlayHud";
import { useHubView } from "../../../hooks/useHubView";
import galleryDef from "@picnic-field/modes/defs/gallery.v1.json";
import {
  addFriend,
  applyReaction,
  degraded as isDegraded,
  loadGauge,
  onGalleryEvent,
  reactionOp,
  removeFriend,
  secondsLeft,
} from "../lib/logic";
import "../../../styles/course.css";
import "../../../styles/gallery.css";
import type { ActivityMap, Point, ReactionMap } from "../types";

/**
 * FS-03 전시관(/gallery) — SCR v0.6 sfs-03(SE) · sfs-10-su(SU 카드 변형).
 *  - FS-03-01 전시관 공간: 받침대 ≤30(공개 스냅샷) · 라벨(별명 · 막힘 빨간 포트) · 미니맵 · 끌기/방향키 이동 · 받침대 접근 → 카드
 *  - FS-03-02 작품 카드: 썸네일 · 작품명 · 원리 태그 · 작동(60초 · 동시 6) · 데려오기 · 스티커(카탈로그 · 1인 1작품 1개)
 *  - FS-03-03 친구 기계 데려오기: 공개 작품 목록 · 자리 5 · 기기 힘 게이지(열화) · 같이 움직이기(로컬 놀이 — 리모컨은 내 기계만)
 * 로컬(PH1)만 — 룸(PH2)·원리 보기·이모트·따라오기는 흐림/숨김. 점수·순위·비교 0(DR-01) · 자유 입력 0(DR-02).
 */
export const GALLERY_MODE_ID = "gallery";

/** 친구 놀이 추적 카메라 — 코스보다 뒤·위에서(친구 기계가 함께 보이게) */
const PLAY_FOLLOW = { back: 2.4, up: 1.5, side: 0.4, lookAhead: 0.3 };

/**
 * 제어 평면이 전시관 모드를 주지 못할 때 쓰는 정의 — 커널이 검증하는 ModeDefinition v1 원본(libs/modes · gallery.v1).
 * 프리셋은 정의의 첫 번째(지금 'hall')를 쓴다: 받침대 관람과 친구 놀이(나 + 친구 ≤5)가 같은 전시관 모드다.
 */
export const FALLBACK_GALLERY_MODE = galleryDef as unknown as ModeDefinition;

interface Data {
  items: GalleryItem[];
  stickers: Sticker[];
  palette: PaletteLike | null;
  config: RuntimeConfig;
  mode: ModeDefinition;
  mine: Snapshot | null;
  myBundle: RunBundle | null;
  exhibits: FriendMachine[];
}

async function modeOr(api: CourseApi, id: string, fallback: ModeDefinition) {
  try {
    return (await api.mode(id)).definition;
  } catch {
    return fallback;
  }
}

/** 동시에 너무 많이 받지 않게(30점) */
async function mapLimit<T, R>(xs: readonly T[], n: number, f: (x: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(xs.length);
  let i = 0;
  await Promise.all(
    Array.from({ length: Math.min(n, xs.length) }, async () => {
      while (i < xs.length) {
        const k = i++;
        out[k] = await f(xs[k] as T);
      }
    }),
  );
  return out;
}

declare global {
  interface Window {
    /** 개발·E2E 계측(가짜 모드 · 개발 서버에서만) */
    __pfGallery?: {
      state: () => { phase: string; active: string[]; selected: string | null; pos: Point | null };
      view: () => ReturnType<GalleryViewLike["stats"]> | null;
    };
  }
}

export function GalleryScreen() {
  const { t, locale } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const session = useSession();
  const courseId = session.status === "ready" ? session.session.courseId : "";
  const social = useSocialApi();
  const fieldApi = useFieldApi();
  const courseApi = useCourseApi();
  const toast = usePicoToast();
  const pico = usePico();

  const [data, setData] = useState<Data | null>(null);
  const prime = usePrimeHub(data?.mine?.glbVersion);   // 스파이크 프라임 판이면 허브 이름도 그 교구로
  const [error, setError] = useState<ErrorCode | null>(null);
  const [seq, setSeq] = useState(0);
  const [phase, setPhase] = useState<"gallery" | "play">("gallery");
  const [pos, setPos] = useState<Point | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const dismissed = useRef<string | null>(null);
  const [active, setActive] = useState<ActivityMap>({});
  const [now, setNow] = useState(() => performance.now());
  const [reactions, setReactions] = useState<ReactionMap>({});
  const [slots, setSlots] = useState<string[]>([]);
  const [fpsWindows, setFpsWindows] = useState<number[]>([]);
  const [myStalls, setMyStalls] = useState<string[]>([]);
  const viewRef = useRef<GalleryViewLike | CourseViewLike | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  useTopBar({
    icon: "🏛",
    title: t("gallery.title"),
    chips: [{ label: t("gallery.chipLocal"), testId: "gallery-chip" }],
  });

  // ── 데이터: 전시관 목록 · 스티커 카탈로그 · 팔레트 · 런타임 설정 · 모드 · 내 작품 · run-bundle ──
  useEffect(() => {
    if (!social || !fieldApi || !courseApi) return;
    let cancelled = false;
    setError(null);
    (async () => {
      const [items, stickers, config, mode, list] = await Promise.all([
        social.gallery(),
        social.stickers().catch(() => [] as Sticker[]),
        courseApi.runtimeConfig(),
        modeOr(courseApi, GALLERY_MODE_ID, FALLBACK_GALLERY_MODE),
        fieldApi.mySnapshots().catch(() => [] as Snapshot[]),
      ]);
      const mine = latestSnapshot(list);
      const bundles = await mapLimit(items, 6, (i) =>
        courseApi.runBundle(i.snapshotId).catch(() => null),
      );
      const exhibits: FriendMachine[] = [];
      items.forEach((i, k) => {
        const b = bundles[k];
        // 라벨 = 서버 별명(실명 없음 — 세션 displayName 은 쓰지 않는다)
        if (b) exhibits.push({ id: i.snapshotId, label: i.nickname, bundle: b });
      });
      const myItem = items.find((i) => i.mine);
      if (myItem) rememberMyNickname(courseId, myItem.nickname);
      let myBundle = myItem ? (bundles[items.indexOf(myItem)] ?? null) : null;
      if (!myBundle && mine)
        myBundle = await courseApi.runBundle(mine.snapshotId).catch(() => null);
      // 작품마다 제 appearance.palette 이름의 팔레트(2026-09-30 팔레트 버전 전환 — 옛 brick-v1 작품은 brick-v1 색)
      const palette = await fieldApi.palettes([
        ...items.map((i) => i.appearance?.palette),
        ...exhibits.map((e) => e.bundle.appearance?.palette),
        myBundle?.appearance?.palette,
        mine?.appearance?.palette,
      ]);
      if (cancelled) return;
      setReactions(Object.fromEntries(items.map((i) => [i.snapshotId, i.myReaction])));
      setData({ items, stickers, palette, config, mode, mine, myBundle, exhibits });
    })().catch((e) => {
      if (!cancelled) setError(toApiError(e).code);
    });
    return () => {
      cancelled = true;
    };
  }, [social, fieldApi, courseApi, seq, courseId]);

  // ── 커널 이벤트 ──
  // 내 허브 출력(SR-RUN-03 — 불빛 · 화면 · 소리)
  const { hub: hubView, now: hubNow, onEvent: onHubOut } = useHubView();
  const onEvent = useCallback(
    (e: KernelEvent) => {
      onHubOut(e);
      if (e.type === "stall") setMyStalls((s) => (s.includes(e.port) ? s : [...s, e.port]));
      if (e.type === "stall-cleared") setMyStalls((s) => s.filter((p) => p !== e.port));
      if (
        e.type === "activated" ||
        e.type === "deactivated" ||
        e.type === "stall-other" ||
        e.type === "activate-busy"
      ) {
        const at = performance.now();
        const r = onGalleryEvent(activeRef.current, e, at);
        activeRef.current = r.active;
        setActive(r.active);
        if (r.busy) toast.show("gallery-busy", t("gallery.card.busy", { n: r.busy.limit }));
        setNow(at);
      }
    },
    [t, toast, onHubOut],
  );
  const world = useHost(onEvent);
  const hostReady = world.status !== "creating" && world.status !== "error";
  // P2-S5 원리 보기(SR-PHY-07) — 작동 중인 작품 · 같이 움직이기(SE 화살표만 · SU 수치)
  const [principleOn, setPrincipleOn] = useState(false);
  const togglePrinciple = useCallback(() => setPrincipleOn((o) => !o), []);
  useHostPrinciple(world.host, principleOn, world.load);

  const galleryReq = useMemo<LoadRequest | null>(
    () =>
      data && data.exhibits.length
        ? {
            mode: data.mode,
            presetKey: data.mode.presets[0]?.key ?? "hall",
            runtimeConfig: data.config,
            seed: 1,
            exhibits: data.exhibits.slice(0, 30),
            record: false,
          }
        : null,
    [data],
  );

  // 전시관 싣기(처음 · 친구 놀이에서 돌아올 때)
  useEffect(() => {
    if (!hostReady || !galleryReq || phase !== "gallery") return;
    let alive = true;
    setActive({});
    void world.loadWorld(galleryReq).then((r) => {
      if (alive && r) world.host()?.start();
    });
    return () => {
      alive = false;
    };
    // world 는 status 가 바뀔 때마다 새 객체 — 준비 여부만 본다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostReady, galleryReq, phase]);

  // 작동 타이머(1초)
  const anyActive = Object.keys(active).length > 0;
  useEffect(() => {
    if (!anyActive) return;
    const h = setInterval(() => setNow(performance.now()), 1000);
    return () => clearInterval(h);
  }, [anyActive]);

  // 기기 힘 — 1초 창 페인트 수(열화 판정 → 그림 품질 낮춤)
  useEffect(() => {
    let last = viewRef.current?.stats().painted ?? 0;
    const h = setInterval(() => {
      const v = viewRef.current;
      if (!v) return;
      const p = v.stats().painted;
      setFpsWindows((w) => [...w.slice(-5), p - last]);
      last = p;
    }, 1000);
    return () => clearInterval(h);
  }, [phase]);
  const degraded = phase === "play" && isDegraded(fpsWindows);
  useEffect(() => {
    viewRef.current?.setQuality(degraded ? "low" : "high");
  }, [degraded]);

  const items = useMemo(() => data?.items ?? [], [data]);
  const byId = useMemo(() => new Map(items.map((i) => [i.snapshotId, i])), [items]);
  const mineIds = useMemo(
    () => new Set(items.filter((i) => i.mine).map((i) => i.snapshotId)),
    [items],
  );
  const maxFriends = data?.config.local.maxFriends ?? 5;

  // 작품명: 교사가 고친 표시 제목(titleOverride — 서버 title 이 모형 slug 가 아님)이면 그 뒤에 별명(와이어 '7차시 크랭크 로봇 · 또또')
  const titleOf = useCallback(
    (i: GalleryItem) => {
      const title = workTitle(t, locale, i, i.nickname);
      return title === i.title && i.nickname && !title.includes(i.nickname)
        ? t("student.upload.workName", { title, name: i.nickname })
        : title;
    },
    [t, locale],
  );

  // 피코 반응 — 친구 작품 카드를 처음 열 때(작품마다 한 번 · SR-ACH-03 gallery-view)
  const viewed = useRef(new Set<string>());
  const selectedItem = selected ? items.find((i) => i.snapshotId === selected) : undefined;
  const reactPico = pico.react;
  useEffect(() => {
    if (!selectedItem || selectedItem.mine || viewed.current.has(selectedItem.snapshotId)) return;
    viewed.current.add(selectedItem.snapshotId);
    reactPico("gallery-view", { key: `pico-view-${selectedItem.snapshotId}` });
  }, [selectedItem, reactPico]);

  const onMove = useCallback((p: Point) => setPos(p), []);
  const onApproach = useCallback((owner: string | null) => {
    // 다가가면 카드 · 멀어지면 닫힘(닫은 카드는 멀어졌다 다시 올 때까지 다시 열지 않는다)
    if (owner == null) {
      dismissed.current = null;
      setSelected(null);
      return;
    }
    if (dismissed.current === owner) return;
    setSelected(owner);
  }, []);
  const pick = useCallback((owner: string) => {
    dismissed.current = null;
    setSelected(owner);
  }, []);

  const activate = (owner: string) => world.host()?.activate(owner);
  const deactivate = (owner: string) => world.host()?.deactivate(owner);
  // 작품 프로그램이 전시관(리모컨 없음)에서 움직일 수 있는가 — 리모컨 전용 · 모터 없음이면 카드에 알린다(2026-10-01)
  const motionOf = useMemo(
    () =>
      new Map<string, RelayMotion>(
        (data?.exhibits ?? []).map((e) => [
          e.id,
          e.bundle.program ? relayMotion(e.bundle.program.ast) : "unknown",
        ]),
      ),
    [data],
  );

  const sticker = async (item: GalleryItem, key: string | null) => {
    if (!social) return;
    const cur = reactions[item.snapshotId] ?? null;
    const op =
      key == null
        ? cur
          ? ({ kind: "delete", snapshotId: item.snapshotId } as const)
          : null
        : reactionOp(cur, item.snapshotId, key, item.mine);
    if (!op) return;
    setReactions((m) => applyReaction(m, op));
    try {
      if (op.kind === "put") {
        await social.setReaction(op.snapshotId, op.stickerKey);
        pico.react("sticker-given", { key: `pico-sticker-${op.snapshotId}-${op.stickerKey}` });
      } else await social.clearReaction(op.snapshotId);
    } catch (e) {
      // 되돌림. FE-305(카탈로그 밖)는 학생에게 보이지 않는다(서버 거부만 — FS-C1) · FE-307(내 작품)은 안내만
      setReactions((m) => ({ ...m, [item.snapshotId]: cur }));
      const code = toApiError(e).code;
      if (code === "FE-307") toast.show("gallery-own", t("fe.FE-307.student.cause"));
      else if (code !== "FE-305") toast.show("gallery-sticker", t("gallery.card.stickerLater"));
    }
  };

  const bring = (id: string) => {
    const r = addFriend(slots, id, maxFriends, mineIds);
    if (r.full) toast.show("gallery-full", t("gallery.friends.full", { max: maxFriends }));
    setSlots(r.slots);
  };

  // ── 친구 놀이(FS-03-03 시작) ──
  const friends = useMemo(
    () =>
      slots
        .map((id) => data?.exhibits.find((e) => e.id === id))
        .filter((e): e is FriendMachine => !!e),
    [slots, data],
  );
  const startPlay = async () => {
    if (!data?.myBundle) return;
    setSelected(null);
    setMyStalls([]);
    setFpsWindows([]);
    setPhase("play");
    const r = await world.loadWorld({
      me: data.myBundle,
      friends,
      // 같이 움직이기는 기계끼리 부딪힌다(2026-10-02 — 친구를 그냥 통과했다). 전시관의 '기계끼리 충돌 없음'(SR-PHY-05)은 받침대 작품용 —
      // 이 장면에는 받침대 작품이 없고 내 기계 + 친구(≤5, 1.2m 간격 자리)뿐이다
      mode: {
        ...data.mode,
        collision: { ...(data.mode.collision as Record<string, unknown>), machineMachine: true },
      },
      presetKey: data.mode.presets[0]?.key ?? "hall",
      runtimeConfig: data.config,
      seed: 2,
      record: false,
    });
    if (r) world.host()?.start();
  };
  const stopPlay = () => {
    world.host()?.pause();
    setFpsWindows([]);
    setPhase("gallery");
  };

  const playing = phase === "play";
  const movable = data?.myBundle ? hasProgram(data.myBundle) : false;
  const remote = useRemote({
    variant: se ? "se" : "su",
    enabled: playing && movable && world.status === "ready",
    maxHz: data?.config.input.maxHz ?? 30,
    send: (s) => sendInput(world, s),
    onStop: stopPlay,
  });

  // 계측(perf) — 전시관 · 친구 놀이 fps p5 · 틱 p95
  usePerfTelemetry(playing ? "play" : "gallery", !!data, async () => {
    const h = world.host();
    return h ? (await h.stats()).tickP95Ms : null;
  });

  // 개발·E2E 계측
  useEffect(() => {
    if (!import.meta.env.DEV && !courseDeps().fake) return;
    window.__pfGallery = {
      state: () => ({ phase, active: Object.keys(active), selected, pos }),
      view: () => viewRef.current?.stats() ?? null,
    };
  });

  if (error)
    return (
      <ScreenRoot id="FS-03-01" title={t("gallery.title")} hideTitle>
        <FeBanner
          code={error}
          audience="student"
          actions={[{ label: t("common.retry"), icon: "⟲", onClick: () => setSeq((n) => n + 1) }]}
        />
      </ScreenRoot>
    );
  if (!data)
    return (
      <ScreenRoot id="FS-03-01" title={t("gallery.title")} hideTitle>
        <div className="pf-pane" aria-busy="true">
          <PicoBubble text={t("gallery.loading")} busy />
        </div>
      </ScreenRoot>
    );

  const sel = selected ? byId.get(selected) : undefined;
  const friendItems = items.filter((i) => !i.mine);
  const panel = (
    <FriendsPanel
      items={friendItems}
      slots={slots}
      max={maxFriends}
      playing={playing}
      hasMachine={!!data.myBundle}
      degraded={degraded}
      gauge={loadGauge(slots.length, maxFriends)}
      onAdd={bring}
      onRemove={(id) => setSlots((s) => removeFriend(s, id))}
      onStart={() => void startPlay()}
      onStop={stopPlay}
    />
  );

  if (playing)
    return (
      <ScreenRoot
        id="FS-03-03"
        title={t("gallery.friends.playTitle")}
        hideTitle
        fill
        focusKey={phase}
        className={`pf-gallery pf-gallery--${density} pf-course--${density} pf-course--driving pf-gallery--play`}
      >
        <div className="pf-gallery__main">
          <div className="pf-gallery__left">
            <CourseScene
              run={world}
              load={world.load}
              bundle={data.myBundle}
              palette={data.palette}
              ghosts={[]}
              friends={friends.map((f) => ({ id: f.id, label: f.label, bundle: f.bundle }))}
              label={t("gallery.friends.sceneLabel")}
              follow={PLAY_FOLLOW}
              onView={(v) => (viewRef.current = v)}
              principle={{ on: principleOn, numbers: !se }}
            >
              <PlayHud
                names={friends.map((f) => f.label)}
                degraded={degraded}
                gauge={loadGauge(slots.length, maxFriends)}
                stalls={myStalls}
                hub={{ view: hubView, now: hubNow, prime }}
                onStop={stopPlay}
                principle={principleOn}
                onPrinciple={togglePrinciple}
              />
              {principleOn && <PrincipleLegend se={se} />}
              {movable ? (
                <RemoteOverlay
                  variant={se ? "se" : "su"}
                  remote={remote}
                  disabled={world.status !== "ready"}
                />
              ) : (
                <div className="pf-hud pf-hud--note" data-testid="hud-static">
                  {t("course.side.static")}
                </div>
              )}
            </CourseScene>
          </div>
        </div>
      </ScreenRoot>
    );

  const empty = items.length === 0;
  const a = sel ? active[sel.snapshotId] : undefined;

  return (
    <ScreenRoot
      id="FS-03-01"
      title={t("gallery.title")}
      hideTitle
      fill
      focusKey={phase}
      className={`pf-gallery pf-gallery--${density}`}
    >
      <div className="pf-gallery__main">
        <div className="pf-gallery__left">
          {empty ? (
            <div className="pf-gallery__scene pf-gallery__scene--empty" data-testid="gallery-empty">
              <div className="pf-pane pf-pane--hero pf-gallery__emptycard">
                <PicoBubble
                  text={t(data.mine ? "gallery.empty.withMine" : "gallery.empty.none")}
                  live={false}
                />
                {!data.mine && (
                  <ButtonLink to="/upload" variant="accent" icon="⬆">
                    {t("student.home.upload")}
                  </ButtonLink>
                )}
              </div>
            </div>
          ) : (
            <GalleryScene
              world={world}
              items={items}
              palette={data.palette}
              active={active}
              selected={selected}
              pos={pos}
              onMove={onMove}
              onApproach={onApproach}
              onPick={pick}
              onView={(v) => (viewRef.current = v)}
              principle={{ on: principleOn, numbers: !se }}
              onPrinciple={togglePrinciple}
            >
              {sel && (
                <ExhibitCard
                  item={sel}
                  title={titleOf(sel)}
                  palette={data.palette}
                  stickers={data.stickers}
                  myReaction={reactions[sel.snapshotId] ?? null}
                  activeS={a ? secondsLeft(a, now) : null}
                  stalled={a?.stalled ?? []}
                  brought={slots.includes(sel.snapshotId)}
                  canBring={slots.length < maxFriends && !sel.static}
                  onActivate={() => activate(sel.snapshotId)}
                  onDeactivate={() => deactivate(sel.snapshotId)}
                  motion={motionOf.get(sel.snapshotId) ?? "unknown"}
                  onBring={() => bring(sel.snapshotId)}
                  onSticker={(k) => void sticker(sel, k)}
                  onClearSticker={() => void sticker(sel, null)}
                  onClose={() => {
                    dismissed.current = sel.snapshotId;
                    setSelected(null);
                  }}
                />
              )}
            </GalleryScene>
          )}
        </div>
        {panel}
      </div>
    </ScreenRoot>
  );
}
